import { motion } from "framer-motion";
import { ArrowDown, ArrowRight } from "lucide-react";
import { Reveal, SectionTag, EASE } from "./Reveal";

const OLD_WAY = [
  "Supplier Search",
  "Price Comparison",
  "Verification",
  "Negotiation",
  "Documentation",
  "Logistics",
  "Delivery",
];

const AJEMI_WAY = [
  { n: "1", title: "Tell us what you need.", text: "Product, quantity, location and timeline — one request." },
  { n: "2", title: "We help source it.", text: "Ajemi identifies and coordinates suitable supply options." },
  { n: "3", title: "You get it delivered.", text: "Delivery is coordinated according to agreed terms." },
];

export default function ProblemSection() {
  return (
    <section id="problem" data-testid="problem-section" className="relative py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Reveal>
          <SectionTag number="01" label="The Problem" />
          <h2 className="font-outfit text-3xl md:text-5xl font-extralight tracking-tight text-white max-w-3xl">
            Sourcing energy shouldn&rsquo;t be <span className="text-gold">complicated.</span>
          </h2>
        </Reveal>

        <div className="mt-16 grid lg:grid-cols-2 gap-10 lg:gap-16 items-start">
          <Reveal delay={0.1}>
            <div className="glass-panel rounded-2xl p-8 md:p-10" data-testid="problem-old-way">
              <p className="text-[11px] tracking-[0.35em] uppercase text-white/40 mb-8">
                The old way — seven moving parts
              </p>
              <div className="space-y-1">
                {OLD_WAY.map((step, i) => (
                  <motion.div
                    key={step}
                    initial={{ opacity: 0, x: -24 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, margin: "-40px" }}
                    transition={{ delay: 0.06 * i, duration: 0.6, ease: EASE }}
                    className="flex flex-col items-start"
                  >
                    <div
                      className="flex items-center gap-4 border border-white/8 bg-white/[0.02] rounded-lg px-5 py-3 w-full"
                      style={{ transform: `translateX(${(i % 3) * 14}px)` }}
                    >
                      <span className="font-outfit text-xs text-white/30">{String(i + 1).padStart(2, "0")}</span>
                      <span className="text-sm text-white/55">{step}</span>
                    </div>
                    {i < OLD_WAY.length - 1 && (
                      <ArrowDown className="w-3.5 h-3.5 text-white/20 ml-8 my-1" />
                    )}
                  </motion.div>
                ))}
              </div>
              <p className="mt-8 text-sm text-white/40 leading-relaxed">
                Every transaction means searching, comparing, verifying, negotiating and
                coordinating — alone.
              </p>
            </div>
          </Reveal>

          <div className="lg:sticky lg:top-28">
            {AJEMI_WAY.map((step, i) => (
              <Reveal key={step.n} delay={0.15 + i * 0.12}>
                <div
                  data-testid={`problem-ajemi-step-${i + 1}`}
                  className="group relative border-l-2 border-gold/50 pl-8 py-7 hover:border-gold transition-colors duration-500"
                >
                  <span className="font-outfit text-6xl md:text-7xl font-extralight text-stroke-volt group-hover:text-gold group-hover:[-webkit-text-stroke:0px] transition-all duration-500">
                    {step.n}
                  </span>
                  <h3 className="font-outfit text-2xl md:text-3xl font-light text-white mt-3">
                    {step.title}
                  </h3>
                  <p className="text-white/55 mt-2 max-w-md">{step.text}</p>
                </div>
              </Reveal>
            ))}
            <Reveal delay={0.5}>
              <div className="mt-6 flex items-center gap-3 pl-8 text-gold text-sm font-outfit tracking-wide">
                <span>One partner. One request.</span>
                <ArrowRight className="w-4 h-4" />
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
